import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { DropdownMenuItem } from '@dealstack/frontend/shared/components/Table/MantineTable/BulkActionsMenu/DropdownMenuItem/DropdownMenuItem';
import { getTableTotalorSelectedCount } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier.tsx';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Flex, Menu, Text, useMantineTheme } from '@mantine/core';
import { Table } from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import type { BulkAction, BulkActionGroup } from './ActionsButton.types';

interface Props<T extends RecordAny> {
	table: Table<T>;
	actions?: (table: Table<T>) => BulkActionGroup<T>;
	showBulkActionItemCount?: boolean;
	showSpecialActions?: boolean;
	actionsMode: 'default' | 'select' | 'action';
	onActionsModeChange: (mode: 'default' | 'select' | 'action') => void;
	showRowSelection: boolean;
	onShowRowSelectionChange: (show: boolean) => void;
	onActionCanceled: () => void;
}

const StyledActionContainer = styled.div<{
	$opened: boolean;
	$actionsMode: 'default' | 'select' | 'action';
	$globalTheme: ReturnType<typeof useMantineTheme>;
	$disabled: boolean;
}>`
	background-color: ${({ $opened, $actionsMode, $globalTheme }) =>
		$opened || $actionsMode === 'action' ? $globalTheme.colors.blue[0] : 'transparent'};
	border: 1px solid;
	border-color: ${({ $opened, $actionsMode, $globalTheme }) =>
		$opened || $actionsMode === 'action'
			? $globalTheme.colors.blue[2]
			: `transparent ${$globalTheme.colors.gray[2]} transparent transparent`};
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	height: 100%;
	width: 10.5rem;
	padding: 0.75rem;
	color: ${({ $opened, $actionsMode, $globalTheme }) => {
		if ($actionsMode === 'action') {
			return defaultTheme.v3.colors.blue[6];
		}
		if ($opened) {
			return $globalTheme.colors.blue[6];
		}
		return $globalTheme.colors.dark[9];
	}};
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${({ $globalTheme }) => $globalTheme.spacing.xs};
	cursor: ${({ $actionsMode, $disabled }) => {
		if ($disabled) {
			return 'not-allowed';
		}

		return $actionsMode === 'select' ? 'default' : 'pointer';
	}};
	opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const ActionButtonIcon = styled(Icon)<{ $actionsMode: 'default' | 'select' | 'action' }>`
	cursor: ${({ $actionsMode }) => ($actionsMode === 'select' ? 'default' : 'pointer')};
	min-width: 1.25rem;
	min-height: 1.25rem;
	color: ${defaultTheme.v3.colors.dark[0]};
	&:hover {
		color: ${({ $actionsMode }) =>
			$actionsMode !== 'default'
				? defaultTheme.v3.colors.blue[6]
				: defaultTheme.v3.colors.dark[0]};
	}
`;

export const ActionsButton = <T extends RecordAny>({
	actions,
	showSpecialActions,
	table,
	actionsMode,
	onActionsModeChange,
	showRowSelection: _showRowSelection,
	onShowRowSelectionChange,
	onActionCanceled,
}: Props<T>) => {
	const tCommonUi = useTranslationCommonUi();
	const globalTheme = useMantineTheme();
	const selectedItemsCount = getTableTotalorSelectedCount(table);
	const [opened, setOpen] = useState(false);
	const [selectedAction, setSelectedAction] = useState<BulkAction<T> | null>(null);
	const resolveCurrentAction = (action: BulkAction<T>): BulkAction<T> => {
		if (!actions) {
			return action;
		}

		const group = actions(table);

		return (
			[group.specialPrimary, group.primary, group.secondary, group.special]
				.flatMap((list) => list ?? [])
				.find((candidate) => candidate.key === action.key) ?? action
		);
	};

	const currentAction =
		actionsMode === 'action' && selectedAction ? resolveCurrentAction(selectedAction) : null;
	const isExecuteDisabled = !!currentAction?.disabled;

	const handleMainClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (actionsMode === 'default') {
			setOpen(true);
		} else if (actionsMode === 'action') {
			if (currentAction && !currentAction.disabled) {
				currentAction.onExecute(table);
			}
		}
	};

	const handleActionSelect = (action: BulkAction<T>) => {
		// If action has executeImmediately flag, run it directly
		if (action.executeImmediately) {
			action.onExecute(table);
			setOpen(false);
			return;
		}

		// Call onSelect callback if provided (e.g., to enable filtering mode)
		action.onSelect?.(table);

		// Regular actions enter selection mode
		setSelectedAction(action);
		setOpen(false);

		onShowRowSelectionChange(true);
		onActionsModeChange('select');
	};

	const handleCloseClick = (e: React.MouseEvent) => {
		e.stopPropagation();

		onShowRowSelectionChange(false);
		onActionsModeChange('default');
		setOpen(false);
		setSelectedAction(null);

		onActionCanceled();
	};

	const selectedActionLabel = selectedAction?.label;
	const selectedActionButtonLabel = selectedAction?.buttonLabel;

	const getButtonText = () => {
		let buttonLabel = '';

		if (selectedActionButtonLabel) {
			buttonLabel =
				typeof selectedActionButtonLabel === 'function'
					? selectedActionButtonLabel()
					: selectedActionButtonLabel;
		} else if (selectedActionLabel) {
			buttonLabel = selectedActionLabel;
		}

		switch (actionsMode) {
			case 'select':
				return 'Select rows';
			case 'action':
				return buttonLabel;
			default:
				return 'Actions';
		}
	};

	const shouldShowMenu = actionsMode === 'default';

	const renderActionItems = (actionList: BulkAction<T>[]) => {
		return actionList.map((action) => (
			<DropdownMenuItem
				key={action.key}
				label={action.label}
				disabled={action.disabled}
				color={action.color}
				dataTestId={action.dataTestId}
				showBulkActionItemCount={action.showBulkActionItemCount}
				table={table}
				callback={() => handleActionSelect(action)}
			/>
		));
	};

	const getActionItems = () => {
		if (!actions) {
			return {
				specialPrimaryItems: [],
				primaryItems: [],
				secondaryItems: [],
				specialItems: [],
			};
		}

		const actionGroup = actions(table);
		const specialPrimaryItems = actionGroup.specialPrimary
			? renderActionItems(actionGroup.specialPrimary)
			: [];
		const primaryItems = actionGroup.primary ? renderActionItems(actionGroup.primary) : [];
		const secondaryItems = actionGroup.secondary
			? renderActionItems(actionGroup.secondary)
			: [];
		const specialItems = actionGroup.special ? renderActionItems(actionGroup.special) : [];
		return { specialPrimaryItems, primaryItems, secondaryItems, specialItems };
	};
	const { specialPrimaryItems, primaryItems, secondaryItems, specialItems } = getActionItems();

	return (
		<Menu
			shadow="md"
			position="bottom-start"
			onClose={() => setOpen(false)}
			opened={opened && shouldShowMenu}
			onOpen={() => shouldShowMenu && setOpen(true)}
			withinPortal
			zIndex={getTableMeta(table).isFullScreen ? Z_INDEX.GLOBAL_MENU : Z_INDEX.MENU}
			offset={0}
		>
			<Menu.Target>
				<StyledActionContainer
					data-testid={TEST_ELEMENT_IDS.CAP_BULK_ACTIONS_MENU_BUTTON}
					$opened={opened}
					$actionsMode={actionsMode}
					$globalTheme={globalTheme}
					$disabled={isExecuteDisabled}
					onClick={handleMainClick}
				>
					<div
						data-testid={
							actionsMode === 'default'
								? undefined
								: TEST_ELEMENT_IDS.CAP_BULK_ACTIONS_MENU_CLOSE_BUTTON
						}
						onClick={actionsMode === 'default' ? undefined : handleCloseClick}
					>
						<ActionButtonIcon
							$actionsMode={actionsMode}
							variant={actionsMode === 'default' ? 'bulls-eye' : 'close'}
							fillStroke
							size="vlg"
						/>
					</div>
					<Text size="B6Bold" style={{ userSelect: 'none', width: '100%' }}>
						{getButtonText()}
					</Text>
					{actionsMode === 'default' && (
						<Icon
							variant="caret-down"
							fillStroke
							size="vlg"
							color={defaultTheme.v3.colors.blue[6]}
						/>
					)}
				</StyledActionContainer>
			</Menu.Target>

			{shouldShowMenu && (
				<Menu.Dropdown
					sx={() => ({
						width: '240px !important',
						border: `1px solid ${defaultTheme.v3.colors.gray[2]}`,
						boxShadow: '0 16px 32px 0 rgba(145, 150, 152, 0.20)',
						overflow: 'hidden',
						padding: 0,
					})}
				>
					<Flex
						align="center"
						justify="space-between"
						gap="xs"
						sx={{
							padding: globalTheme.spacing.sm,
						}}
					>
						<Flex align="center" gap="xs">
							<Text
								size="B6Bold"
								style={{
									color: defaultTheme.v3.colors.dark[8],
									userSelect: 'none',
								}}
							>
								{tCommonUi(
									'mantine_table.top_toolbar.bulk_actions_menu.bulk_actions'
								)}
							</Text>
						</Flex>
					</Flex>

					<Menu.Divider
						sx={{
							margin: 0,
							marginLeft: '0.25rem',
							marginRight: '0.25rem',
						}}
					/>

					{!selectedItemsCount ? (
						<>
							<Menu.Item>
								<div
									style={{
										backgroundColor: globalTheme.colors.orange[0],
										padding: globalTheme.spacing.xs,
										maxWidth: '100%',
									}}
								>
									<div
										style={{
											borderLeft: `2px solid ${globalTheme.colors.orange[6]}`,
											padding: `${globalTheme.spacing.xs} 4px`,
										}}
									>
										<Text size="B6Medium" style={{ userSelect: 'none' }}>
											{tCommonUi(
												'mantine_table.top_toolbar.bulk_actions_menu.alert'
											)}
										</Text>
									</div>
								</div>
							</Menu.Item>

							<Menu.Divider
								sx={{
									margin: 0,
								}}
							/>
						</>
					) : null}

					<div
						style={{
							margin: globalTheme.spacing.xs,
						}}
					>
						{specialPrimaryItems.length > 0 ? specialPrimaryItems : null}

						{specialPrimaryItems.length > 0 &&
						primaryItems.length > 0 &&
						!showSpecialActions ? (
							<Menu.Divider />
						) : null}

						{primaryItems.length > 0 ? primaryItems : null}

						{primaryItems.length > 0 &&
						secondaryItems.length > 0 &&
						!showSpecialActions ? (
							<Menu.Divider />
						) : null}

						{secondaryItems.length > 0 ? secondaryItems : null}

						{secondaryItems.length > 0 && specialItems.length > 0 ? (
							<Menu.Divider />
						) : null}

						{specialItems.length > 0 ? specialItems : null}
					</div>
				</Menu.Dropdown>
			)}
		</Menu>
	);
};
